import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { Button, Input, cn } from '@jeraisonne/ui';
import { VideoTile } from './video-tile';
import type { JoinMediaChoice } from '../video/use-visio-room';

/**
 * Écran d'entrée : on se voit AVANT de rejoindre, on choisit micro/caméra ouverts ou non — ni
 * l'un ni l'autre n'est jamais exigé. Ouvre son PROPRE aperçu (indépendant de `useVisioRoom`, qui
 * n'ouvre le flux « réel » de l'appel qu'à `startJoin` — deux `getUserMedia` de suite, mais un
 * hook qui reste simple et testable sans notion d'aperçu).
 *
 * `allowNameEdit` (pour un invité, Phase D — pas encore branché pour un compte connecté, dont le
 * nom vient de son profil) : si fourni, un champ « Ton nom » apparaît, requis pour continuer.
 */
export function PrejoinScreen({
  title = 'Prêt à entrer ?',
  onJoin,
  allowNameEdit = false,
  name = '',
  onNameChange,
}: {
  title?: string;
  onJoin: (media: JoinMediaChoice) => void;
  allowNameEdit?: boolean;
  name?: string;
  onNameChange?: (name: string) => void;
}) {
  const [wantAudio, setWantAudio] = useState(true);
  const [wantVideo, setWantVideo] = useState(true);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (!wantAudio && !wantVideo) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setPreviewStream(null);
      return;
    }
    void navigator.mediaDevices
      .getUserMedia({ audio: wantAudio, video: wantVideo })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        setPreviewStream(stream);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('[prejoin] aperçu indisponible', err);
        setError('Caméra/micro indisponibles — tu pourras réessayer une fois dans la salle.');
        setPreviewStream(null);
      });
    return () => {
      cancelled = true;
    };
  }, [wantAudio, wantVideo]);

  // L'aperçu est purement local à cet écran : on l'arrête à la sortie (rejoint ou démonté),
  // `startJoin` (côté appelant) ouvrira son propre flux pour l'appel.
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const nameValid = !allowNameEdit || name.trim().length > 0;

  function handleJoin() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onJoin({ audio: wantAudio, video: wantVideo });
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-6 sm:py-8">
      <p className="text-ink-900 text-lg font-semibold">{title}</p>

      <VideoTile
        stream={previewStream}
        label="Aperçu"
        muted
        mirrored
        cameraOn={wantVideo && !!previewStream}
        micOn={wantAudio}
        className="max-w-sm"
      />

      {error && <p className="text-accent-700 max-w-sm text-center text-xs">{error}</p>}

      {allowNameEdit && (
        <div className="w-full max-w-sm space-y-1">
          <label htmlFor="prejoin-name" className="text-ink-700 text-sm font-medium">
            Ton nom
          </label>
          <Input
            id="prejoin-name"
            value={name}
            onChange={(e) => onNameChange?.(e.target.value)}
            placeholder="Comment veux-tu apparaître ?"
            className="min-h-[2.75rem]"
          />
        </div>
      )}

      <div className="flex w-full max-w-sm gap-2">
        <button
          type="button"
          onClick={() => setWantAudio((v) => !v)}
          aria-pressed={wantAudio}
          className={cn(
            'flex min-h-[2.75rem] flex-1 flex-col items-center justify-center gap-1 rounded-xl border transition-colors active:scale-[0.98]',
            wantAudio
              ? 'border-primary-200 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-600',
          )}
        >
          {wantAudio ? <Mic className="size-5" /> : <MicOff className="size-5" />}
          <span className="text-xs font-semibold">Micro</span>
        </button>
        <button
          type="button"
          onClick={() => setWantVideo((v) => !v)}
          aria-pressed={wantVideo}
          className={cn(
            'flex min-h-[2.75rem] flex-1 flex-col items-center justify-center gap-1 rounded-xl border transition-colors active:scale-[0.98]',
            wantVideo
              ? 'border-primary-200 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-600',
          )}
        >
          {wantVideo ? <Video className="size-5" /> : <VideoOff className="size-5" />}
          <span className="text-xs font-semibold">Caméra</span>
        </button>
      </div>

      <Button
        onClick={handleJoin}
        disabled={!nameValid}
        className="min-h-[2.75rem] w-full max-w-sm"
      >
        Rejoindre la salle
      </Button>
    </div>
  );
}
